import { formatUserDateTime, formatUserTime } from './user-time-format'
import type { PreferredHourCycle } from '@shared/ipc/routers/BaseIpc.router'

/** The longest delay `setTimeout` takes; a timer for a later date re-arms itself. */
export const MAX_TIMEOUT_MS = 2_147_483_647

/** Where a due date stands: past, or still ahead on today's date. Further out, it's neither. */
export type DueState = 'today' | 'overdue'

export function dueStateOf(scheduledAt: Date | undefined, now: number = Date.now()): DueState | undefined {
  if (!scheduledAt) return undefined
  if (scheduledAt.getTime() <= now) return 'overdue'
  return scheduledAt.toDateString() === new Date(now).toDateString() ? 'today' : undefined
}

/** When `dueStateOf` next changes for this date: the midnight it falls due on, then the moment it does. */
export function nextDueStateChange(scheduledAt: Date | undefined, now: number = Date.now()): number | undefined {
  if (!scheduledAt || scheduledAt.getTime() <= now) return undefined
  const dueDay = new Date(scheduledAt).setHours(0, 0, 0, 0)
  return dueDay > now ? dueDay : scheduledAt.getTime()
}

/** How close a due date has to be before it reads as urgent. */
export const DUE_DATE_HIGHLIGHT_WINDOW_MS = 60 * 60 * 1000

/** Short form for the due-date pill — time of day when it lands today, else the date. */
export function formatDatePill(scheduledAt: Date, hourCycle: PreferredHourCycle | null): string {
  const now = new Date()
  const isToday = scheduledAt.toDateString() === now.toDateString()
  if (isToday) {
    return formatUserTime(scheduledAt, hourCycle)
  }
  return scheduledAt.toLocaleDateString([], { month: 'short', day: 'numeric' })
}

export function formatDateTooltip(scheduledAt: Date, hourCycle: PreferredHourCycle | null): string {
  return formatUserDateTime(scheduledAt, hourCycle)
}

export function shouldHighlightDueDate(scheduledAt: Date): boolean {
  const diff = scheduledAt.getTime() - Date.now()
  return diff < DUE_DATE_HIGHLIGHT_WINDOW_MS
}
